
import { Component, Vue } from 'vue-property-decorator';
import Gravatar from 'vue-gravatar';
import { Action, State } from 'vuex-class';
import { actions, mutations } from '@/store/types';
import { UpdateUserActionType } from '@/types/store/actions';
import { filterObj } from '@/utils/filter-obj';
import { UserInfo } from '@/types/user-info';
import { upload } from '@/utils/upload-file';
import { timezonesOptions } from '@/constants/timezones';
import { countriesOptions } from '@/constants/countries';
import UserQuickActions from '@/components/quick-actions/UserQuickActions.vue';
import PasswordPopup from '@/components/users/popups/PasswordPopup.vue';
import EmailPopup from '@/components/users/popups/EmailPopup.vue';
import TwoFactorAuthPopup from '@/components/users/popups/TwoFactorAuthPopup.vue';
import LoginSettingsPopup from '@/components/users/popups/LoginSettingsPopup.vue';
import * as dlg from '@/modules/dlg';
import api from '@/modules/api';
import _get from 'lodash.get';
// import { Subscription } from '@/types/subscriptions';
// import { SubscriptionStatus } from '@/constants/subscription_status';
import { PaymentUser } from '@/types/payment';
import { getCurrency } from '@/utils/currency';
import { getErrorMessage } from '@/utils/common';
import { userRoles } from '@/constants/userRoles';
import { Subscription } from '@/types/subscriptions';

@Component({
    components: { UserQuickActions, Gravatar, PasswordPopup, EmailPopup, TwoFactorAuthPopup, LoginSettingsPopup },
})
export default class UserAccountSettings extends Vue {
    name = '';
    email = '';
    country: string | undefined = '';
    timezone: string | undefined = '';
    profilePicture: string | undefined = '';
    twoFactorEnabled = false;
    is2FAVerified = false;
    // protected stripePaymentUser: PaymentUser | null = null;
    timezonesOptions = timezonesOptions;
    countriesOptions = countriesOptions;
    passwordDialog = false;
    emailDialog = false;
    twoFaDialog = false;
    LoginSettingsDialog = false;
    disableCountry = false;
    protected getCurrency = getCurrency;

    // client API key
    showKey = false;
    loadingKey = false;
    clientAPIKey = '';
    keyStatus = false;

    @State('user')
    user!: UserInfo;

    @State('subscriptions')
    userSubscriptions!: Subscription[];

    @Action(actions.UPDATE_USER)
    updateUser!: UpdateUserActionType;

    async mounted() {
        if (this.user) {
            this.name = this.user.name;
            this.email = this.user.email;
            this.country = this.user.country;
            this.timezone = this.user.timezone;
            this.profilePicture = this.user.profilePicture;
            this.twoFactorEnabled = this.user.twoFactorEnabled;
            this.is2FAVerified = this.user.is2FAVerified;
            this.keyStatus = this.user?.keyActive ?? false;
            // this.disableCountry = this.user.country ? true : false;
        }
        await this.getPaymentCustomer();
    }

    async getPaymentCustomer() {
        try {
            this.$store.commit(mutations.LOADING, { loading: true });
            // const paymentUser = await api.getPaymentUser();
            // this.stripePaymentUser = paymentUser.paymentUser ?? null;
            // this.disableCountry = this.stripePaymentUser?.sourceData.currency ? true : false;
            this.disableCountry = this.userSubscriptions.length ? true : false;
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error, `Something went wrong while getting stripe customer`));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    get isEnterprise() {
        return this.user?.roles && this.user.roles.length > 0 && this.user.roles.includes(userRoles.Enterprise);
    }

    get isGenerated() {
        return this.user?.keyGenerated == true;
    }

    get image() {
        return this.profilePicture ? `data:image/png;base64,${this.profilePicture}` : null;
    }

    hideKey() {
        this.showKey = false;
        this.loadingKey = false;
        this.clientAPIKey = '';
        this.keyStatus = false;
    }

    async generateKey() {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            const response = await api.generateClientAPIKey();
            this.clientAPIKey = response.data.key;
            this.keyStatus = response.data.active;
            this.showKey = true;
            this.$store.commit(mutations.SET_USER, { user: { ...this.user, keyGenerated: true } });
            Vue.toasted.success('Generated successfully');
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async revealKey() {
        this.loadingKey = true;
        try {
            const response = await api.fetchClientAPIKey();
            this.clientAPIKey = response.data.key;
            this.keyStatus = response.data.active;
            this.showKey = true;
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.loadingKey = false;
        }
    }

    async removeKey() {
        const confirmed = await dlg.confirm('Please confirm you want to remove the API key.', 'Remove API key');
        if (!confirmed) return;

        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.removeClientAPIKey();
            this.clientAPIKey = '';
            this.showKey = false;
            this.keyStatus = false;
            this.$store.commit(mutations.SET_USER, { user: { ...this.user, keyGenerated: false } });
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async handleKeyStatusChange() {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            const response = await api.updateClientAPIKey(this.keyStatus);
            this.clientAPIKey = response.data.key;
            this.keyStatus = response.data.active;
        } catch (error) {
            this.keyStatus = !this.keyStatus;
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async uploadFile({ target: { files } }: { target: { files: File[] } }) {
        if (files.length > 0) {
            const base64 = await upload(files[0]);
            if (!base64) return;
            this.profilePicture = base64.replace(/data.*?base64,/, '');
        }
    }

    async save(e: Event) {
        e.preventDefault();
        const updatedUser = await this.updateUser({
            user: filterObj(
                {
                    name: this.name,
                    country: this.country,
                    currency: this.getCurrency(this.country ?? ''),
                    timezone: this.timezone,
                    profilePicture: this.profilePicture,
                    email: this.email,
                },
                { dropNull: true, dropUndefined: true },
            ),
        });
        // if (updatedUser == true && this.country) this.disableCountry = true;
    }

    async twoFactorAuthDone(result: boolean) {
        this.twoFactorEnabled = result;
    }
    async disable2fa() {
        const confirmed = await dlg.confirm(
            'Please confirm you want to unlink authentication device',
            'Disable two-facrtor authentication',
        );
        if (!confirmed) {
            return;
        }
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.twoFactorAuthUnlink();
            const { userDetails } = await api.loadUser();
            this.$store.commit(mutations.SET_USER, { user: userDetails });
            this.is2FAVerified = false;
            Vue.toasted.success(`Unlinked successfully`);
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }
}
